import React from "react";
import { getCurrentLangName, getFlagByCountryCode, getStudentName } from "helpers/String.helper";
import LoadingComponent from "commons/loading/Loading.component";
import { getProfileImageUrl } from "helpers/Image.helper";
import { useNavigate } from "react-router-dom";

interface StudentInfoPopupProps {
    onClose: () => void;
    info: any;
    isLoading: boolean;
    errorMessage?: string;
    onExpel?: (studentId: number) => void;
}

const StudentInfoPopup: React.FC<StudentInfoPopupProps> = ({ 
    onClose, 
    info, 
    isLoading, 
    errorMessage,
    onExpel
}) => {
    const navigate = useNavigate();

    const handleExpel = () => {
        if (onExpel && info?.userStudent?.id) {
            onExpel(info.userStudent.id);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 w-screen h-screen top-0 left-0 p-4 !m-0">
            <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">
                        학생 정보
                    </h3>
                    <button 
                        className="text-gray-400 hover:text-gray-600" 
                        onClick={onClose}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {isLoading ? (
                    <LoadingComponent />
                ) : errorMessage ? (
                    <div className="flex flex-col items-center justify-center py-10 text-gray-500">
                        <p className="text-center">{errorMessage}</p>
                    </div>
                ) : (
                    <div className="flex flex-col items-center">
                        <div className="w-32 h-32 rounded-full bg-gray-200 overflow-hidden mb-4">
                            <img
                                src={getProfileImageUrl(info?.userStudent?.profileImage, 200)}
                                alt={getStudentName(info?.userStudent)}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    e.currentTarget.src = '/imgs/dummy/basic-img.png';
                                }}
                            />
                        </div>

                        <h2 className="mb-2 text-center">
                            <span className="text-xl font-bold">
                                {getStudentName(info?.userStudent)}
                            </span>
                            <br />
                            <span className="text-sm text-gray-600">
                                {info?.userStudent?.birthday ? 
                                    `(${new Date().getFullYear() - new Date(info.userStudent.birthday).getFullYear()}세)` : 
                                    ""
                                }
                            </span>
                        </h2>

                        <p className="text-gray-600 mb-4 text-center">
                            <span className="text-2xl mr-2">
                                {getFlagByCountryCode(info?.userStudent?.countryCode)}
                            </span>
                            {getCurrentLangName(info?.userStudent?.country?.names)}
                        </p>

                        {info?.userStudent?.genderTypeCode && (
                            <p className="text-gray-600 mb-4 text-center">
                                {info.userStudent.genderTypeCode === 'genderType:boy' ? '남성' : 
                                 info.userStudent.genderTypeCode === 'genderType:girl' ? '여성' : '성별 정보 없음'}
                            </p>
                        )}

                        {info?.userStudent?.lastProfileUpdatedAt && (
                            <p className="text-gray-500 text-sm mb-6 text-center">
                                마지막 업데이트: {new Date(info.userStudent.lastProfileUpdatedAt).toLocaleDateString('ko-KR')}
                            </p>
                        )}

                        <div className="flex flex-row gap-4 w-full">
                            <button
                                className="px-4 py-2 rounded-lg w-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
                                onClick={() => {
                                    onClose();
                                    if (info?.userStudent?.id) {
                                        navigate(`/school/student/${info.userStudent.id}`);
                                    }
                                }}
                            >
                                상세 정보 보기
                            </button>
                            {onExpel && (
                                <button
                                    className="px-4 py-2 rounded-lg w-full text-sm font-medium bg-red-500 text-white hover:bg-red-600"
                                    onClick={handleExpel}
                                >
                                    퇴원처리
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StudentInfoPopup;
